import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal, X, Eye, Upload, Trash2, RotateCcw, History, UserPlus, ArchiveRestore } from "lucide-react";
import { T, prettyType, chartStatusInfo, fmtUploadedLong, titleCase } from "./pdUtil";

/* ───────────── checkbox (matches Sort&Filter type checkbox) ───────────── */
export function CheckBox({ checked, onChange, testid, indeterminate }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate && !checked; }, [indeterminate, checked]);
  return (
    <button ref={ref} type="button" data-testid={testid} onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className="w-[16px] h-[16px] rounded-[4px] flex items-center justify-center shrink-0"
      style={{ border: `1.5px solid ${checked || indeterminate ? T.indigo : T.mute}`, background: checked || indeterminate ? T.indigo : "transparent" }}
      aria-checked={checked} role="checkbox">
      {checked ? <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="3"><path d="M3 8.5 6.5 12 13 4" strokeLinecap="round" strokeLinejoin="round" /></svg>
        : indeterminate ? <span style={{ width: 8, height: 2, background: "#fff", borderRadius: 2 }} /> : null}
    </button>
  );
}

/* ───────────── per-row actions menu (fixed-position portal, never clipped) ───────────── */
export function RowMenu({ row, deletedTab, onOpen, onIngest, onRemove, onRetry, onActivity, onRestore, onDelete, onLink }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ci = chartStatusInfo(row);
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return undefined;
    const place = () => {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: Math.min(r.bottom + 4, window.innerHeight - 220), left: Math.max(8, r.right - 200) });
    };
    place();
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      // Also keep open when clicking inside the portal panel (Item buttons).
      const panel = document.querySelector(`[data-testid="pd-row-menu-panel-${row.id}"]`);
      if (panel && panel.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", place, true); window.addEventListener("resize", place);
    document.addEventListener("mousedown", onDoc);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); document.removeEventListener("mousedown", onDoc); };
  }, [open]);

  const Item = ({ icon: Icon, label, onClick, danger, testid }) => (
    <button type="button" data-testid={testid} onClick={(e) => { e.stopPropagation(); setOpen(false); onClick(); }}
      className="w-full flex items-center gap-2 px-3 py-2 rounded-[8px] text-[13px] hover:bg-black/[.04]" style={{ color: danger ? T.red : T.ink }}>
      <Icon size={14} /> {label}
    </button>
  );

  const Txt = ({ label, onClick, danger, testid }) => (
    <button type="button" role="menuitem" data-testid={testid} onClick={(e) => { e.stopPropagation(); setOpen(false); onClick(); }}
      className="w-full text-left px-2.5 py-2 rounded-[7px] text-[13px] hover:bg-black/[.04]" style={{ color: danger ? T.red : T.ink, minHeight: 36 }}>{label}</button>
  );
  const items = [<Txt key="open" label="Open" onClick={() => onOpen(row)} testid={`pd-row-open-${row.id}`} />];
  if (deletedTab) items.push(<Txt key="restore" label="Restore" onClick={() => onRestore(row)} testid={`pd-row-restore-${row.id}`} />);
  else if (onLink) {
    items.push(<Txt key="link" label="Link to a patient…" onClick={() => onLink(row)} testid={`pd-row-link-${row.id}`} />);
    items.push(<Txt key="del" label="Delete…" danger onClick={() => onDelete(row)} testid={`pd-row-delete-${row.id}`} />);
  } else {
    if (ci.key === "ready" || ci.key === "removed") items.push(<Txt key="ing" label="Add to chart" onClick={() => onIngest(row)} testid={`pd-row-ingest-${row.id}`} />);
    if (ci.key === "ingested" || ci.key === "nothing") items.push(<Txt key="rm" label="Remove from chart…" onClick={() => onRemove(row)} testid={`pd-row-remove-${row.id}`} />);
    if (ci.key === "failed") items.push(<Txt key="retry" label="Retry" onClick={() => onRetry(row)} testid={`pd-row-retry-${row.id}`} />);
    items.push(<Txt key="act" label="Activity" onClick={() => onActivity(row)} testid={`pd-row-activity-${row.id}`} />);
    items.push(<Txt key="del" label="Delete…" danger onClick={() => onDelete(row)} testid={`pd-row-delete-${row.id}`} />);
  }

  return (
    <>
      <button ref={btnRef} type="button" data-testid={`pd-row-menu-${row.id}`} aria-label="Document actions"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} aria-expanded={open}
        className="pd-rmb" style={{ color: T.ink }}>
        <MoreHorizontal size={16} />
      </button>
      {open ? createPortal(
        <div role="menu" className="fixed z-[9999] p-1 rounded-[10px] w-[200px]" style={{ top: pos.top, left: pos.left, background: T.card, border: `1px solid ${T.hair}`, boxShadow: T.shadow }} data-testid={`pd-row-menu-panel-${row.id}`}>
          {items}
        </div>, document.body) : null}
    </>
  );
}

/* ───────────── bulk action bar ───────────── */
export function BulkBar({ count, deletedTab, busy, onIngest, onDelete, onRestore, onClear }) {
  if (!count) return null;
  return (
    <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: `1px solid ${T.hair}`, background: "#F3F3FD" }} data-testid="pd-bulk-bar">
      <span className="text-[12.5px] font-semibold" style={{ color: T.ink }}>{count} selected</span>
      <span className="flex-1" />
      {deletedTab ? (
        <button type="button" data-testid="pd-bulk-restore" disabled={busy} onClick={onRestore} className="h-[30px] px-3 rounded-[9px] text-[12.5px] font-semibold text-white disabled:opacity-50" style={{ background: T.ink }}>Restore</button>
      ) : (
        <>
          <button type="button" data-testid="pd-bulk-ingest" disabled={busy} onClick={onIngest} className="h-[30px] px-3 rounded-[9px] text-[12.5px] font-semibold text-white disabled:opacity-50" style={{ background: T.ink }}>Add to chart</button>
          <button type="button" data-testid="pd-bulk-delete" disabled={busy} onClick={onDelete} className="h-[30px] px-3 rounded-[9px] text-[12.5px] font-semibold disabled:opacity-50" style={{ border: `1px solid ${T.hair}`, color: T.red }}>Delete</button>
        </>
      )}
      <button type="button" data-testid="pd-bulk-clear" onClick={onClear} className="h-[30px] px-2.5 rounded-[9px] text-[12.5px] font-semibold" style={{ color: T.sub }}>Clear</button>
    </div>
  );
}

/* ───────────── modal shell ───────────── */
function Modal({ title, sub, onClose, children, testid }) {
  return createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 pd-scrim" style={{ background: "rgba(20,20,18,.34)" }} onMouseDown={onClose} data-testid={testid}>
      <div className="pd-scope w-[460px] max-w-full max-h-[80vh] flex flex-col rounded-[16px] overflow-hidden" style={{ background: T.card, border: `1px solid ${T.hair}`, boxShadow: T.shadow }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-5 h-[52px] shrink-0" style={{ borderBottom: `1px solid ${T.hair}` }}>
          <div className="min-w-0">
            <div className="text-[14px] font-bold truncate" style={{ color: T.ink }}>{title}</div>
            {sub ? <div className="text-[11.5px] truncate" style={{ color: T.sub }}>{sub}</div> : null}
          </div>
          <span className="flex-1" />
          <button type="button" onClick={onClose} data-testid="pd-modal-close" className="w-7 h-7 rounded-[8px] flex items-center justify-center hover:bg-black/[.05]"><X size={16} style={{ color: T.mute }} /></button>
        </div>
        <div className="flex-1 overflow-auto p-5">{children}</div>
      </div>
    </div>, document.body);
}

function CountGrid({ title, map, color }) {
  const entries = Object.entries(map || {}).filter(([, v]) => v);
  if (!entries.length) return null;
  return (
    <div className="mb-3">
      <div className="text-[10.5px] font-bold uppercase tracking-[.04em] mb-1.5" style={{ color: T.mute }}>{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {entries.map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1 text-[12px] font-semibold px-2 py-1 rounded-[8px]" style={{ background: T.soft, color: color || T.ink }}>
            <span className="tabular-nums">{v}</span> {prettyType(k)}
          </span>
        ))}
      </div>
    </div>
  );
}

export function PreviewModal({ row, data, loading, onClose }) {
  const added = data?.added || data?.would_add || data?.to_add;
  const skipped = data?.skipped || data?.already_present;
  const summary = data?.summary || data?.detail;
  return (
    <Modal title="Chart data preview" sub={row?.filename} onClose={onClose} testid="pd-preview-modal">
      {loading ? <div className="text-[13px] py-6 text-center" style={{ color: T.mute }}>Loading preview…</div> : (
        <div>
          {summary ? <div className="text-[13px] mb-3" style={{ color: T.ink }}>{summary}</div> : null}
          <CountGrid title="Would be added" map={added} color={T.ink} />
          <CountGrid title="Already on the chart (skipped)" map={skipped} color={T.sub} />
          {!summary && !added && !skipped ? <pre className="text-[11.5px] overflow-auto" style={{ color: T.sub }}>{JSON.stringify(data, null, 2)}</pre> : null}
        </div>
      )}
    </Modal>
  );
}

export function ActivityModal({ row, data, loading, onClose }) {
  const entries = data?.activity || data?.entries || [];
  return (
    <Modal title="Activity trail" sub={row?.filename} onClose={onClose} testid="pd-activity-modal">
      {loading ? <div className="text-[13px] py-6 text-center" style={{ color: T.mute }}>Loading…</div>
        : !entries.length ? <div className="text-[13px] py-6 text-center" style={{ color: T.mute }}>No chart activity yet.</div>
        : (
          <div className="space-y-3">
            {entries.map((e, i) => (
              <div key={e.id || i} className="rounded-[10px] p-3" style={{ border: `1px solid ${T.hair}`, background: T.rail }} data-testid={`pd-activity-row-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[12.5px] font-bold capitalize" style={{ color: T.ink }}>{String(e.action || "").replace(/_/g, " ")}</span>
                  <span className="flex-1" />
                  <span className="text-[11.5px] tabular-nums" style={{ color: T.mute }}>{fmtUploadedLong(e.at || e.time)}</span>
                </div>
                <div className="text-[11.5px] mt-0.5" style={{ color: T.sub }}>{e.by || e.user_name || e.email || "—"}{e.is_sub_login ? " · sub-login" : ""}{e.ip ? ` · ${e.ip}` : ""}</div>
                {e.details?.summary ? <div className="text-[12px] mt-1.5" style={{ color: T.ink }}>{e.details.summary}</div> : null}
                {e.details?.patient?.name ? <div className="text-[11.5px] mt-0.5" style={{ color: T.sub }}>Patient {titleCase(e.details.patient.name)}{e.details.patient.mrn ? ` · MRN ${e.details.patient.mrn}` : ""}</div> : null}
              </div>
            ))}
          </div>
        )}
    </Modal>
  );
}
